import { useEffect, useState } from 'react';

import { QrCode } from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

interface RegistrarPagamentoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cobranca: { numeroDamsp: string; valor: string } | null;
  /** Chamado com a forma escolhida; quem chama decide como atualizar a cobrança (sem API ainda). */
  onConfirmar: (formaPagamento: 'PIX' | 'BOLETO') => void;
}

/**
 * Modal de registrar pagamento de uma cobrança DAMSP. Sem API ainda: só
 * confirma a forma de pagamento e devolve para o detalhe atualizar a
 * situação localmente.
 */
export function RegistrarPagamentoModal({
  open,
  onOpenChange,
  cobranca,
  onConfirmar,
}: RegistrarPagamentoModalProps) {
  const [formaPagamento, setFormaPagamento] = useState('');

  useEffect(() => {
    if (open) setFormaPagamento('');
  }, [open]);

  const handleConfirmar = () => {
    if (formaPagamento !== 'PIX' && formaPagamento !== 'BOLETO') return;
    onConfirmar(formaPagamento);
    onOpenChange(false);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <QrCode className="h-5 w-5" aria-hidden="true" />
          Registrar pagamento{cobranca?.numeroDamsp ? ` — ${cobranca.numeroDamsp}` : ''}
        </span>
      }
      description="Confirme a forma de pagamento para liquidar esta cobrança."
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleConfirmar} disabled={!formaPagamento}>
            Confirmar pagamento
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg bg-muted p-4 text-center">
          <p className="mb-1 text-sm text-muted-foreground">Valor</p>
          <MoneyDisplay value={cobranca?.valor} className="text-2xl font-bold" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pagamento-forma">Forma de pagamento *</Label>
          <Select value={formaPagamento} onValueChange={setFormaPagamento}>
            <SelectTrigger id="pagamento-forma">
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PIX">PIX</SelectItem>
              <SelectItem value="BOLETO">Boleto</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pagamento-comprovante">Comprovante (opcional)</Label>
          <Input id="pagamento-comprovante" type="file" accept=".pdf,.jpg,.png" />
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default RegistrarPagamentoModal;
